import { useEffect, useRef } from "react";
import { API_URL } from "../../api/auth";
import { useAuthToken } from "../use-auth-token";

// Layer C of the sandbox-keepalive defense-in-depth: while the FE is looking
// at a session, ping the backend so it refreshes the e2b pause window. Covers
// the "user watching, agent idle between messages" case that the gateway-side
// activity extender can't see (no work happening → no events).
//
// We ping every 30s when the tab is visible. When the tab is hidden browsers
// throttle setInterval to ~1/min anyway, so we switch to 60s so the schedule
// matches reality and Safari's more aggressive background throttling is less
// likely to silently starve us.

const VISIBLE_INTERVAL_MS = 30_000;
const HIDDEN_INTERVAL_MS = 60_000;

type TerminalStatus = "completed" | "aborted" | "error" | "killed";
const TERMINAL_STATUSES: ReadonlySet<string> = new Set<TerminalStatus>([
  "completed",
  "aborted",
  "error",
  "killed",
]);

export interface UseArgusSessionHeartbeatOptions {
  sessionId: string | undefined;
  // Current session status — when terminal, we stop pinging.
  sessionStatus?: string | null;
  // Escape hatch for tests / force-disable from the caller.
  enabled?: boolean;
}

export function useArgusSessionHeartbeat({
  sessionId,
  sessionStatus,
  enabled = true,
}: UseArgusSessionHeartbeatOptions) {
  const token = useAuthToken();
  // Stabilize the token for the effect so we don't tear down / re-arm
  // every render when Clerk reissues an identical token.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!enabled) return;
    if (!sessionId) return;
    if (sessionStatus && TERMINAL_STATUSES.has(sessionStatus)) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Returns true when the schedule should be cancelled — server told us
    // (via 410 Gone) that the session/instance is terminal and further
    // heartbeats would just keep auto-resuming a sandbox the cleanup
    // crons want paused.
    const ping = async (): Promise<boolean> => {
      const currentToken = tokenRef.current;
      if (!currentToken) return false;
      try {
        const resp = await fetch(`${API_URL}/client/argus/sessions/${sessionId}/heartbeat`, {
          method: "POST",
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        if (resp.status === 410) {
          if (import.meta.env.DEV) console.debug("[argus-heartbeat] 410 Gone — stopping schedule");
          return true;
        }
      } catch (err) {
        // Best-effort — a 404 after the session ends or a transient network
        // blip shouldn't spam the console. Only log at debug level.
        if (import.meta.env.DEV) console.debug("[argus-heartbeat] ping failed:", err);
      }
      return false;
    };

    const scheduleNext = () => {
      if (cancelled) return;
      const delay =
        typeof document !== "undefined" && document.visibilityState === "hidden"
          ? HIDDEN_INTERVAL_MS
          : VISIBLE_INTERVAL_MS;
      timer = setTimeout(async () => {
        const stop = await ping();
        if (stop) {
          cancelled = true;
          return;
        }
        scheduleNext();
      }, delay);
    };

    // Ping immediately on mount / status-transition-to-active so the sandbox
    // is refreshed before the first 30s even elapses. Also ping whenever
    // the tab becomes visible after being hidden — this is the scenario
    // that prompted Layer C (backgrounded tab → sandbox paused).
    void (async () => {
      const stop = await ping();
      if (stop) {
        cancelled = true;
        return;
      }
      scheduleNext();
    })();

    const onVisibilityChange = () => {
      if (cancelled) return;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (document.visibilityState === "visible") {
        void (async () => {
          const stop = await ping();
          if (stop) {
            cancelled = true;
            return;
          }
          scheduleNext();
        })();
      } else {
        scheduleNext();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [sessionId, sessionStatus, enabled]);
}
